import React from "react";
import Typography from "Components/Atoms/Typography";
import { useTranslation } from "react-i18next";
import { getGroupSizeColor } from "../groupSizeColor";
import { getCompactIntervals } from "../intervalSummary";

type Props = { intervals: NonNullable<ReturnType<typeof getCompactIntervals>> };

export default function CompactIntervals({ intervals }: Props) {
  const { t } = useTranslation("dashboard");
  const maxGroups = Math.max(1, ...intervals.flatMap(interval => interval.sizes.map(size => size.count)));
  return (
    <div className="compact-intervals" aria-label={t("Groups by arrival time")}>
      {intervals.map(({ block, time, groups, guests, sizes }) => (
        <section key={block} className="compact-interval" aria-label={time}>
          <div className="compact-interval-header">
            <Typography variant="h5" translation={null}>
              {time}
            </Typography>
            <div className="compact-interval-totals">
              <Typography variant="text-2" weight="bold" translation={null}>
                {t("Guest count", { count: guests })}
              </Typography>
              <Typography variant="text-3" color="subdued" translation={null}>
                {t("Group count", { count: groups })}
              </Typography>
            </div>
          </div>
          <Typography variant="text-4" color="subdued" translation="dashboard">
            Group size (guests)
          </Typography>
          <Typography variant="text-4" color="subdued" translation="dashboard" block>
            Number of groups
          </Typography>
          <ul
            className="compact-interval-sizes"
            aria-label={t("Group sizes")}
            tabIndex={0}
          >
            {sizes.map(({ guests, count }) => (
              <li
                key={guests}
                title={t("Group count", { count }) + " · " + t("Guest count", { count: guests })}
                aria-label={count + " × " + t("Guest count", { count: guests })}
              >
                <div className="compact-interval-plot" aria-hidden="true">
                  <div
                    className="compact-interval-column"
                    style={{
                      height: (count / maxGroups) * 100 + "%",
                      background: getGroupSizeColor(guests),
                    }}
                  >
                    <span className="compact-interval-count">{count}</span>
                  </div>
                </div>
                <Typography variant="text-4" translation={null}>
                  {guests}
                </Typography>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
